import { useEffect, useRef, useState } from "react";
import { motion, useAnimationControls, useReducedMotion, useTransform } from "framer-motion";

const IMG_W = 1264;
const IMG_H = 848;
const EASE = [0.16, 1, 0.3, 1];

// Box of each animal inside the original 1264x848 artwork (percentages) + pivot point.
const ANIMALS = {
  toucan: { left: 11.87, top: 8.25, width: 26.9, height: 41.27, origin: "32% 66%" },
  monkey: { left: 64.87, top: 15.92, width: 18.99, height: 31.25, origin: "94% 6%" },
};

const BURSTS = {
  toucan: { y: [0, -26, 0, -12, 0], rotate: [0, -9, 7, -4, 0], scale: [1, 1.04, 0.98, 1.02, 1] },
  monkey: { rotate: [0, 16, -12, 7, 0], x: [0, 10, -8, 3, 0], scale: [1, 1.05, 1, 1.02, 1] },
};
const IDLE = {
  toucan: { y: [0, -4, 0], rotate: [0, -2, 0] },
  monkey: { rotate: [0, 4, -3, 0] },
};

// Mimics object-fit: cover + object-position so the animal layers sit exactly on the artwork.
const useCoverBox = (ref, posX) => {
  const [box, setBox] = useState(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const update = () => {
      const { width: cw, height: ch } = el.getBoundingClientRect();
      const s = Math.max(cw / IMG_W, ch / IMG_H);
      const w = IMG_W * s;
      const h = IMG_H * s;
      const px = window.innerWidth >= 1024 ? 0.5 : posX;
      setBox({ w, h, left: (cw - w) * px, top: 0 });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref, posX]);
  return box;
};

const Animal = ({ name, src, alt, sx, sy, near, poke, reduce, onTalk }) => {
  const a = ANIMALS[name];
  const burst = useAnimationControls();
  const idle = useAnimationControls();
  const rot = useTransform(sx, (v) => v * (name === "monkey" ? 10 : 7));
  const lift = useTransform(sy, (v) => v * (name === "monkey" ? 6 : -8));

  useEffect(() => {
    if (reduce) return undefined;
    idle.start({ ...IDLE[name], transition: { duration: name === "monkey" ? 3.6 : 2.8, repeat: Infinity, ease: "easeInOut" } });
    return () => idle.stop();
  }, [idle, name, reduce]);

  useEffect(() => {
    if (reduce || !near) return undefined;
    burst.start({ ...BURSTS[name], transition: { duration: 1.1, ease: "easeInOut" } });
    return undefined;
  }, [near, burst, name, reduce]);

  useEffect(() => {
    if (reduce || !poke) return undefined;
    burst.start({ ...BURSTS[name], transition: { duration: 1.1, ease: "easeInOut" } });
    return undefined;
  }, [poke, burst, name, reduce]);

  return (
    <motion.div data-testid={`hero-animal-${name}`} data-near={near ? "true" : "false"}
      className="absolute pointer-events-none"
      style={{ left: `${a.left}%`, top: `${a.top}%`, width: `${a.width}%`, height: `${a.height}%`, rotate: reduce ? 0 : rot, y: reduce ? 0 : lift, transformOrigin: a.origin }}>
      <motion.div animate={idle} className="w-full h-full" style={{ transformOrigin: a.origin }}>
        <button type="button" data-testid={`hero-animal-${name}-button`} aria-label={`${alt}: ver consejo de cultivo del día`}
          onClick={(e) => { if (!reduce) burst.start({ ...BURSTS[name], transition: { duration: 0.9, ease: "easeInOut" } }); onTalk(name, e.currentTarget.getBoundingClientRect()); }}
          className="block w-full h-full pointer-events-auto cursor-pointer rounded-[40%] focus-visible:outline-2 focus-visible:outline-[#F2B441]">
          <motion.img animate={burst} src={src} alt={alt} draggable={false}
            className="w-full h-full select-none drop-shadow-[0_10px_18px_rgba(0,0,0,0.35)]" style={{ transformOrigin: a.origin }} />
        </button>
      </motion.div>
    </motion.div>
  );
};

export const JHeroScene = ({ sx, sy, pointer, poke, onTalk }) => {
  const wrap = useRef(null);
  const stage = useRef(null);
  const reduce = useReducedMotion();
  const box = useCoverBox(wrap, 0.22);
  const [near, setNear] = useState({ toucan: false, monkey: false });

  useEffect(() => {
    if (!pointer || !stage.current) {
      setNear((p) => (p.toucan || p.monkey ? { toucan: false, monkey: false } : p));
      return undefined;
    }
    const r = stage.current.getBoundingClientRect();
    const next = {};
    Object.entries(ANIMALS).forEach(([k, a]) => {
      const cx = r.left + (r.width * (a.left + a.width / 2)) / 100;
      const cy = r.top + (r.height * (a.top + a.height / 2)) / 100;
      const radius = Math.max((r.width * a.width) / 100, (r.height * a.height) / 100) * 0.75;
      next[k] = Math.hypot(pointer.x - cx, pointer.y - cy) < radius;
    });
    setNear((p) => (p.toucan === next.toucan && p.monkey === next.monkey ? p : next));
    return undefined;
  }, [pointer]);

  return (
    <motion.div ref={wrap} initial={{ scale: 1.25 }} animate={{ scale: 1.05 }} transition={{ duration: 2.6, ease: EASE }}
      className="absolute inset-0 overflow-hidden">
      <img src="/img/jungle-hero-bg.webp" alt="Selva Jungle Nutrient con el tucán, la jirafa, el mono y el elefante"
        className="w-full h-full object-cover object-[22%_top] lg:object-top" />
      {box && (
        <div ref={stage} className="absolute" style={{ width: box.w, height: box.h, left: box.left, top: box.top }}>
          <Animal name="toucan" src="/img/hero-toucan.webp" alt="Tucán" sx={sx} sy={sy} near={near.toucan} poke={poke} reduce={reduce} onTalk={onTalk} />
          <Animal name="monkey" src="/img/hero-monkey.webp" alt="Mono" sx={sx} sy={sy} near={near.monkey} poke={poke} reduce={reduce} onTalk={onTalk} />
        </div>
      )}
    </motion.div>
  );
};
